import React from 'react';
import { CaretLeft, CaretRight, CaretDoubleLeft, CaretDoubleRight } from '@phosphor-icons/react';

/**
 * Reusable client-side pagination component.
 *
 * Usage:
 *   const { currentPage, setCurrentPage, paginated, totalPages } = usePagination(items, 20);
 *   <Pagination currentPage={currentPage} totalPages={totalPages} onChange={setCurrentPage} />
 */
export const usePagination = (items, pageSize = 20) => {
  const [currentPage, setCurrentPage] = React.useState(1);
  const safeItems = Array.isArray(items) ? items : [];
  const totalPages = Math.max(1, Math.ceil(safeItems.length / pageSize));

  // Reset to page 1 when items change drastically
  React.useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  const start = (currentPage - 1) * pageSize;
  const paginated = safeItems.slice(start, start + pageSize);

  return {
    currentPage,
    setCurrentPage,
    paginated,
    totalPages,
    pageSize,
    totalItems: safeItems.length,
    rangeStart: safeItems.length === 0 ? 0 : start + 1,
    rangeEnd: Math.min(start + pageSize, safeItems.length),
  };
};

const Pagination = ({ currentPage, totalPages, onChange, rangeStart, rangeEnd, totalItems, accentColor = '#E63946' }) => {
  if (totalPages <= 1 && (!totalItems || totalItems <= 0)) return null;

  const goTo = (n) => {
    const next = Math.min(Math.max(1, n), totalPages);
    onChange(next);
  };

  // Compact page range: show current ± 1 with ellipses
  const getRange = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
    const sorted = [...pages].filter(p => p >= 1 && p <= totalPages).sort((a, b) => a - b);
    const out = [];
    sorted.forEach((p, i) => {
      if (i > 0 && p - sorted[i - 1] > 1) out.push('...');
      out.push(p);
    });
    return out;
  };

  const btnBase = 'min-w-[36px] h-9 px-2 text-sm font-medium border transition-colors flex items-center justify-center';

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6 pt-4 border-t border-gray-100" data-testid="pagination">
      <p className="text-xs text-gray-500" data-testid="pagination-info">
        {totalItems > 0 ? `${rangeStart}-${rangeEnd} sur ${totalItems}` : 'Aucun résultat'}
      </p>
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          <button
            onClick={() => goTo(1)}
            disabled={currentPage === 1}
            className={`${btnBase} border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed`}
            data-testid="pagination-first"
            aria-label="Première page"
          >
            <CaretDoubleLeft size={14} />
          </button>
          <button
            onClick={() => goTo(currentPage - 1)}
            disabled={currentPage === 1}
            className={`${btnBase} border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed`}
            data-testid="pagination-prev"
            aria-label="Page précédente"
          >
            <CaretLeft size={14} />
          </button>
          {getRange().map((p, i) =>
            p === '...' ? (
              <span key={`e-${i}`} className="px-2 text-gray-400 text-sm">…</span>
            ) : (
              <button
                key={p}
                onClick={() => goTo(p)}
                className={`${btnBase} ${
                  p === currentPage
                    ? 'text-white border-transparent'
                    : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
                style={p === currentPage ? { backgroundColor: accentColor } : undefined}
                data-testid={`pagination-page-${p}`}
              >
                {p}
              </button>
            )
          )}
          <button
            onClick={() => goTo(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`${btnBase} border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed`}
            data-testid="pagination-next"
            aria-label="Page suivante"
          >
            <CaretRight size={14} />
          </button>
          <button
            onClick={() => goTo(totalPages)}
            disabled={currentPage === totalPages}
            className={`${btnBase} border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed`}
            data-testid="pagination-last"
            aria-label="Dernière page"
          >
            <CaretDoubleRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default Pagination;
